import { useState, useCallback } from 'react'
import { lumi } from '@/lib/lumi'

export interface Application {
  _id: string
  vacancyId: string
  vacancyTitle?: string
  companyUserId: string
  companyName?: string
  collaboratorUserId: string
  collaboratorName?: string
  collaboratorEmail?: string
  cvUrl?: string
  coverNote?: string
  status: 'pendiente' | 'revisado' | 'entrevista' | 'aceptado' | 'rechazado'
  // Entrevista
  interviewDate?: string
  interviewAddress?: string
  interviewPhone?: string
  interviewNotes?: string
  interviewStatus?: 'pendiente_confirmacion' | 'confirmada' | 'rechazada' | 'asistio' | 'no_asistio'
  createdAt: string
  updatedAt: string
}

export const APPLICATION_STATUS_LABELS: Record<Application['status'], string> = {
  pendiente: 'Pendiente',
  revisado: 'Revisado',
  entrevista: 'Entrevista',
  aceptado: 'Aceptado',
  rechazado: 'Rechazado',
}

export const APPLICATION_STATUS_STYLES: Record<Application['status'], string> = {
  pendiente: 'bg-slate-100 text-slate-600',
  revisado: 'bg-blue-50 text-blue-700',
  entrevista: 'bg-violet-50 text-violet-700',
  aceptado: 'bg-emerald-50 text-emerald-700',
  rechazado: 'bg-red-50 text-red-700',
}

export const INTERVIEW_STATUS_LABELS: Record<NonNullable<Application['interviewStatus']>, string> = {
  pendiente_confirmacion: 'Pendiente de confirmación',
  confirmada: 'Confirmada',
  rechazada: 'Rechazada por candidato',
  asistio: 'Asistió',
  no_asistio: 'No asistió',
}

export const INTERVIEW_STATUS_STYLES: Record<NonNullable<Application['interviewStatus']>, string> = {
  pendiente_confirmacion: 'bg-amber-50 text-amber-700',
  confirmada: 'bg-emerald-50 text-emerald-700',
  rechazada: 'bg-red-50 text-red-600',
  asistio: 'bg-blue-50 text-blue-700',
  no_asistio: 'bg-slate-100 text-slate-500',
}

export function useApplications() {
  const [applications, setApplications] = useState<Application[]>([])
  const [loading, setLoading] = useState(false)

  const fetchByCompany = useCallback(async (companyUserId: string) => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.applications.list({
        filter: { companyUserId },
        sort: { createdAt: -1 },
      })
      setApplications(list as Application[])
    } catch (e) {
      console.error('Error al cargar postulantes:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchByCollaborator = useCallback(async (collaboratorUserId: string) => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.applications.list({
        filter: { collaboratorUserId },
        sort: { createdAt: -1 },
      })
      setApplications(list as Application[])
    } catch (e) {
      console.error('Error al cargar postulaciones:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  const updateStatus = useCallback(async (id: string, status: Application['status']) => {
    return await lumi.entities.applications.update(id, {
      status,
      updatedAt: new Date().toISOString(),
    })
  }, [])

  const updateInterview = useCallback(
    async (
      id: string,
      data: {
        interviewDate?: string
        interviewAddress?: string
        interviewPhone?: string
        interviewNotes?: string
        interviewStatus?: Application['interviewStatus']
        status?: Application['status']
      },
    ) => {
      return await lumi.entities.applications.update(id, {
        ...data,
        updatedAt: new Date().toISOString(),
      })
    },
    [],
  )

  const withdrawApplication = useCallback(async (id: string) => {
    return await lumi.entities.applications.delete(id)
  }, [])

  return {
    applications,
    loading,
    fetchByCompany,
    fetchByCollaborator,
    updateStatus,
    updateInterview,
    withdrawApplication,
  }
}
